import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Landmark, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [signupEnabled, setSignupEnabled] = useState(null);

  useEffect(() => {
    api
      .get("/settings/access")
      .then(({ data }) => setSignupEnabled(!!data.signup_enabled))
      .catch(() => setSignupEnabled(true));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="civic-hero hidden lg:flex flex-col justify-between p-12 text-white">
        <Link to="/" className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-md bg-white/15 flex items-center justify-center">
            <Landmark className="w-6 h-6" />
          </span>
          <div>
            <p className="font-heading font-semibold">Cheltenham Township</p>
            <p className="text-sm text-slate-200">Tax Filing Portal</p>
          </div>
        </Link>
        <div>
          <h2 className="font-heading text-3xl font-semibold tracking-tight leading-tight">
            File in minutes, not hours
          </h2>
          <p className="mt-4 text-slate-200 max-w-md leading-relaxed">
            Register your business to file the Mercantile &amp; Business Privilege Tax Return and
            keep a copy of every filing.
          </p>
        </div>
        <p className="text-sm text-slate-300">Montgomery County, Pennsylvania</p>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">
            Create account
          </h1>
          <p className="mt-2 text-slate-500">For Cheltenham Township merchants.</p>

          {signupEnabled === false ? (
            <div className="mt-8 rounded-md border border-slate-200 bg-slate-50 p-6" data-testid="signups-closed-card">
              <span className="w-11 h-11 rounded-md bg-blue-50 flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-[#1E3A8A]" />
              </span>
              <h2 className="font-heading text-lg font-medium text-slate-900">Signups are currently closed</h2>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                This is a demonstration site and new merchant registration is temporarily unavailable.
                Please check back later or sign in with an existing account.
              </p>
              <Button
                onClick={() => navigate("/login")}
                data-testid="closed-to-login-button"
                className="mt-5 w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
              >
                Go to sign in
              </Button>
            </div>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Full name</Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input"
                placeholder="Jane Merchant"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input"
                placeholder="you@business.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input"
                placeholder="At least 6 characters"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create account
            </Button>
          </form>
          )}

          <p className="mt-6 text-sm text-slate-500">
            Already registered?{" "}
            <Link to="/login" className="text-[#1E3A8A] font-medium hover:underline" data-testid="to-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
